import { Table, Tag, Tree, Typography } from 'antd'
import type { DataNode } from 'antd/es/tree'
import type { ColumnsType } from 'antd/es/table'
import type {
  EquipmentLine,
  FinishLine,
  FixtureLine,
  OpeningLine,
  RoomNode,
  SpaceBill,
} from '@/types/api'

const { Text } = Typography

const SIGNAGE_SPACE_ID = 'SIGN'
const SIGNAGE_CATEGORY = '招牌'
const CATEGORY_ORDER = ['地面', '天花', '墙面', '招牌', '工艺', '灯具', '门窗', '安装', '家具', '水牌', '电器'] as const

function isWallBaseProcess(name?: string | null): boolean {
  return Boolean(name) && name.includes('墙面基层')
}

function isColorPanelOverlay(name?: string | null): boolean {
  const text = name || ''
  return text.includes('多彩板') && (text.includes('仿木纹') || text.includes('仿水泥'))
}

function overlayWallBaseQty(rows: BillRow[]): BillRow[] {
  const panel = rows
    .filter((r) => (r.unit === 'm²' || r.unit === 'm2') && isColorPanelOverlay(r.name))
    .reduce((sum, r) => sum + r.qty, 0)
  if (panel <= 0) return rows
  const area = Math.round(panel * 100) / 100
  return rows.map((r) =>
    isWallBaseProcess(r.name) ? { ...r, qty: area, unit: 'm²' } : r,
  )
}

function drawingName(raw?: string | null, fallback = ''): string {
  const name = String(raw || '')
    .replace(/\\[NnP]/g, ' ')
    .replace(/[\r\n]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  return name || fallback
}

type BillRow = {
  key: string
  category: string
  name: string
  qty: number
  unit: string
  remark: string
  sheetNos: string[]
}

function unionSheetNos(...groups: (string[] | undefined | null)[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const g of groups) {
    for (const n of g || []) {
      const t = String(n || '').trim()
      if (!t || seen.has(t)) continue
      seen.add(t)
      out.push(t)
    }
  }
  return out
}

function walkRooms(rooms: RoomNode[], visit: (room: RoomNode) => void): void {
  for (const room of rooms) {
    visit(room)
    walkRooms(room.children, visit)
  }
}

function isSignageRoom(room: RoomNode): boolean {
  return room.space_id === SIGNAGE_SPACE_ID || room.name === SIGNAGE_CATEGORY
}

function collectFinishEntries(bill: SpaceBill): { finish: FinishLine; signage: boolean }[] {
  const out: { finish: FinishLine; signage: boolean }[] = []
  walkRooms(bill.rooms, (room) => {
    const signage = isSignageRoom(room)
    for (const f of room.finishes) out.push({ finish: f, signage })
  })
  for (const f of bill.unbound_finishes || []) {
    out.push({ finish: f, signage: false })
  }
  return out
}

function collectFixtureEntries(bill: SpaceBill): FixtureLine[] {
  const out: FixtureLine[] = []
  walkRooms(bill.rooms, (room) => {
    if (isSignageRoom(room)) return
    out.push(...(room.fixtures || []))
  })
  out.push(...(bill.unbound_fixtures || []))
  return out
}

function collectLines(bill: SpaceBill): {
  openings: OpeningLine[]
  equipment: EquipmentLine[]
} {
  const openings: OpeningLine[] = []
  const equipment: EquipmentLine[] = []
  walkRooms(bill.rooms, (room) => {
    openings.push(...(room.openings || []))
    equipment.push(...(room.equipment || []))
  })
  openings.push(...(bill.unbound_openings || []))
  equipment.push(...(bill.unbound_equipment || []))
  return { openings, equipment }
}

type FinishEntry = { finish: FinishLine; signage: boolean }

function isRoomSplitFloorFinish(f: FinishLine): boolean {
  return /地面防水层|局部地面抬高|地面局部抬高/.test(drawingName(f.drawing_name))
}

function shopFinishMergeName(f: FinishLine): string {
  const name = drawingName(f.drawing_name)
  if (/局部地面抬高|地面局部抬高/.test(name)) return '局部地面抬高'
  return name
}

function mergeFinishEntries(entries: FinishEntry[]): FinishEntry[] {
  const map = new Map<string, FinishEntry>()
  for (const entry of entries) {
    const f = entry.finish
    const mergeName = shopFinishMergeName(f)
    const key = `${entry.signage ? 'sign' : 'room'}|${mergeName}|${f.surface}|${f.qty_unit || ''}`
    const prev = map.get(key)
    if (!prev) {
      map.set(key, {
        finish: {
          ...f,
          drawing_name: mergeName || f.drawing_name,
          remarks: [...(f.remarks || [])],
          sheet_nos: [...(f.sheet_nos || [])],
        },
        signage: entry.signage,
      })
      continue
    }
    if (f.area_m2 > prev.finish.area_m2) {
      prev.finish.source = f.source
      prev.finish.qty_unit = f.qty_unit || prev.finish.qty_unit
    }
    prev.finish.area_m2 = Math.round((prev.finish.area_m2 + f.area_m2) * 100) / 100
    if (f.qty_unit && !prev.finish.qty_unit) prev.finish.qty_unit = f.qty_unit
    prev.finish.sheet_nos = unionSheetNos(prev.finish.sheet_nos, f.sheet_nos)
    for (const r of f.remarks || []) {
      const text = r.trim()
      if (text && !(prev.finish.remarks || []).includes(text)) {
        prev.finish.remarks = [...(prev.finish.remarks || []), text]
      }
    }
  }
  return [...map.values()].sort(
    (a, b) =>
      b.finish.area_m2 - a.finish.area_m2 ||
      a.finish.drawing_name.localeCompare(b.finish.drawing_name, 'zh'),
  )
}

function mergeFixtureEntries(entries: FixtureLine[]): FixtureLine[] {
  const map = new Map<string, FixtureLine>()
  for (const f of entries) {
    const key = `${f.code}|${drawingName(f.drawing_name)}|${f.is_install ? '1' : '0'}`
    const prev = map.get(key)
    if (!prev) {
      map.set(key, { ...f, remarks: [...(f.remarks || [])], sheet_nos: [...(f.sheet_nos || [])] })
      continue
    }
    prev.count += f.count
    prev.sheet_nos = unionSheetNos(prev.sheet_nos, f.sheet_nos)
    for (const r of f.remarks || []) {
      const text = r.trim()
      if (text && !(prev.remarks || []).includes(text)) {
        prev.remarks = [...(prev.remarks || []), text]
      }
    }
  }
  return [...map.values()].sort(
    (a, b) =>
      b.count - a.count ||
      a.code.localeCompare(b.code) ||
      a.drawing_name.localeCompare(b.drawing_name, 'zh'),
  )
}

function mergeOpenings(lines: OpeningLine[]): OpeningLine[] {
  const map = new Map<string, OpeningLine>()
  for (const o of lines) {
    const key = `${o.kind}|${o.code || ''}|${drawingName(o.drawing_name)}|${o.is_install ? '1' : '0'}`
    const prev = map.get(key)
    if (!prev) {
      map.set(key, { ...o, remarks: [...(o.remarks || [])], sheet_nos: [...(o.sheet_nos || [])] })
      continue
    }
    prev.count += o.count
    prev.sheet_nos = unionSheetNos(prev.sheet_nos, o.sheet_nos)
    for (const r of o.remarks || []) {
      const text = r.trim()
      if (text && !(prev.remarks || []).includes(text)) {
        prev.remarks = [...(prev.remarks || []), text]
      }
    }
  }
  return [...map.values()].sort((a, b) => a.kind.localeCompare(b.kind) || b.count - a.count)
}

function mergeEquipment(lines: EquipmentLine[]): EquipmentLine[] {
  const map = new Map<string, EquipmentLine>()
  for (const e of lines) {
    const key = `${equipmentCategory(e)}|${equipmentName(e)}|${equipmentUnit(e)}`
    const prev = map.get(key)
    if (!prev) {
      map.set(key, { ...e, remarks: [...(e.remarks || [])], sheet_nos: [...(e.sheet_nos || [])] })
      continue
    }
    prev.count += e.count
    prev.sheet_nos = unionSheetNos(prev.sheet_nos, e.sheet_nos)
    for (const r of e.remarks || []) {
      const text = r.trim()
      if (text && !(prev.remarks || []).includes(text)) {
        prev.remarks = [...(prev.remarks || []), text]
      }
    }
  }
  return [...map.values()].sort(
    (a, b) => a.kind.localeCompare(b.kind) || b.count - a.count || a.drawing_name.localeCompare(b.drawing_name, 'zh'),
  )
}

function categoryOfFinish(f: FinishLine, signage: boolean): string {
  if (signage) return SIGNAGE_CATEGORY
  if (isRoomSplitFloorFinish(f)) return '地面'
  if (f.source === 'process_note') return '工艺'
  if (f.surface === 'ceiling') return '天花'
  if (f.surface === 'floor') return '地面'
  if (f.surface === 'wall') return '墙面'
  if (f.source === 'callout') return '墙面'
  return '地面'
}

function finishName(f: FinishLine): string {
  return drawingName(f.drawing_name)
}

function withInstallPrefix(label: string, isInstall?: boolean): string {
  const text = (label || '').trim()
  if (!isInstall || !text) return text
  if (text.startsWith('安装') || text.startsWith('(安装)') || text.startsWith('（安装）')) return text
  return `安装 ${text}`
}

function fixtureName(f: FixtureLine): string {
  return drawingName(f.drawing_name, (f.code || '').trim() ? `[${(f.code || '').trim()}]` : '')
}

function isElectricalFixture(f: FixtureLine): boolean {
  return (f.code || '').toUpperCase().startsWith('EL-')
}

function equipmentCategory(e: EquipmentLine): string {
  if (e.space_id === SIGNAGE_SPACE_ID) return SIGNAGE_CATEGORY
  if (e.kind === 'appliance') return '电器'
  if (e.kind === 'water_sign') return '水牌'
  return '家具'
}

function equipmentUnit(e: EquipmentLine): string {
  if (e.unit) return e.unit
  if (e.kind === 'appliance') return '台'
  if (e.kind === 'water_sign') return '个'
  return '件'
}

function equipmentName(e: EquipmentLine): string {
  return drawingName(e.drawing_name)
}

function buildRows(bill: SpaceBill): BillRow[] {
  const { openings, equipment } = collectLines(bill)
  const rows: BillRow[] = []
  for (const [i, { finish: f, signage }] of mergeFinishEntries(collectFinishEntries(bill)).entries()) {
    const cat = categoryOfFinish(f, signage)
    const processItem = f.source === 'process_note' && !(f.area_m2 > 0)
    rows.push({
      key: `fin-${cat}-${i}`,
      category: cat,
      name: finishName(f),
      qty: processItem ? 1 : f.area_m2,
      unit: processItem ? '项' : f.qty_unit === 'm' ? 'm' : 'm²',
      remark: (f.remarks || []).map((r) => r.trim()).filter(Boolean).join('；'),
      sheetNos: unionSheetNos(f.sheet_nos),
    })
  }
  for (const [i, f] of mergeFixtureEntries(collectFixtureEntries(bill)).entries()) {
    const electrical = isElectricalFixture(f)
    rows.push({
      key: `fx-${i}`,
      category: f.is_install ? '安装' : electrical ? '电器' : '灯具',
      name: fixtureName(f),
      qty: f.count,
      unit: electrical ? '个' : '盏',
      remark: (f.remarks || []).map((r) => r.trim()).filter(Boolean).join('；'),
      sheetNos: unionSheetNos(f.sheet_nos),
    })
  }
  for (const [i, o] of mergeOpenings(openings).entries()) {
    rows.push({
      key: `op-${i}`,
      category: o.is_install ? '安装' : '门窗',
      name: openingName(o),
      qty: o.count,
      unit: o.unit || '樘',
      remark: openingRemark(o),
      sheetNos: unionSheetNos(o.sheet_nos),
    })
  }
  for (const [i, e] of mergeEquipment(equipment).entries()) {
    rows.push({
      key: `eq-${i}`,
      category: equipmentCategory(e),
      name: equipmentName(e),
      qty: e.count,
      unit: equipmentUnit(e),
      remark: (e.remarks || []).map((r) => r.trim()).filter(Boolean).join('；'),
      sheetNos: unionSheetNos(e.sheet_nos),
    })
  }
  const rank = new Map<string, number>(CATEGORY_ORDER.map((c, i) => [c, i]))
  const collapsed = overlayWallBaseQty(collapseSameNameRows(rows))
  collapsed.sort(
    (a, b) =>
      (rank.get(a.category) ?? 99) - (rank.get(b.category) ?? 99) ||
      a.name.localeCompare(b.name, 'zh'),
  )
  return collapsed
}

function collapseSameNameRows(rows: BillRow[]): BillRow[] {
  const map = new Map<string, BillRow>()
  for (const row of rows) {
    const key = `${row.category}|${row.name}|${row.unit}`
    const prev = map.get(key)
    if (!prev) {
      map.set(key, { ...row, sheetNos: [...row.sheetNos] })
      continue
    }
    prev.qty = Math.round((prev.qty + row.qty) * 100) / 100
    prev.sheetNos = unionSheetNos(prev.sheetNos, row.sheetNos)
    const remarks = [prev.remark, row.remark].map((r) => r.trim()).filter(Boolean)
    prev.remark = [...new Set(remarks)].join('；')
  }
  return [...map.values()].map((row, i) => ({ ...row, key: `${row.category}-${i}` }))
}

function sheetColumns(onOpenSheets?: (nos: string[]) => void): ColumnsType<BillRow> {
  return [
    { title: '类别', dataIndex: 'category', width: 72 },
    { title: '材料 / 工艺 / 名称', dataIndex: 'name', ellipsis: true },
    {
      title: '图号',
      dataIndex: 'sheetNos',
      width: 132,
      render: (nos: string[]) =>
        nos?.length ? (
          <span>
            {nos.map((n) => (
              <Tag
                key={n}
                color="blue"
                style={{ cursor: onOpenSheets ? 'pointer' : 'default', marginInlineEnd: 4 }}
                onClick={() => onOpenSheets?.(nos)}
              >
                {n}
              </Tag>
            ))}
          </span>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: '数量',
      dataIndex: 'qty',
      width: 88,
      align: 'right',
      render: (v: number, r) => (r.unit === 'm²' ? v.toFixed(2) : String(v)),
    },
    { title: '单位', dataIndex: 'unit', width: 56 },
    {
      title: '备注',
      dataIndex: 'remark',
      ellipsis: true,
      render: (v: string) => v || <Text type="secondary">—</Text>,
    },
  ]
}

interface Props {
  bill: SpaceBill
  onOpenSheets?: (sheetNos: string[]) => void
}

export function countShopBillRows(bill: SpaceBill): number {
  return buildRows(bill).length
}

export function countRoomBillRooms(bill: SpaceBill): number {
  let n = 0
  walkRooms(bill.rooms, () => {
    n += 1
  })
  return n
}

export function countRoomBillLines(bill: SpaceBill): number {
  let n = 0
  walkRooms(bill.rooms, (room) => {
    n += room.finishes.length
    n += (room.fixtures || []).length
    n += (room.openings || []).length
    n += (room.equipment || []).length
  })
  n += (bill.unbound_finishes || []).length
  n += (bill.unbound_fixtures || []).length
  n += (bill.unbound_openings || []).length
  n += (bill.unbound_equipment || []).length
  return n
}

export function ShopBillTree({ bill, onOpenSheets }: Props) {
  const rows = buildRows(bill)
  return (
    <>
      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        {bill.shop_name}：{bill.shop_area_m2.toFixed(2)} m²
        <Tag style={{ marginLeft: 8 }}>{rows.length} 条</Tag>
      </Text>
      <Table<BillRow>
        size="small"
        pagination={false}
        rowKey="key"
        columns={sheetColumns(onOpenSheets)}
        dataSource={rows}
        scroll={{ y: 360 }}
        locale={{ emptyText: '尚未识别到材料或工艺' }}
      />
      <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
        总汇总按全店加总，不按房间拆行。房间明细见上方列表。面层用面积，灯具/门窗/家具/电器用件数；招牌面层按填充平方，店招/机柜按个。点图号可看对应图纸预览。
        {rows.length === 0 && (
          <Tag color="orange" style={{ marginLeft: 8 }}>尚未识别到材料或工艺</Tag>
        )}
      </Text>
    </>
  )
}

function qtyText(qty: number, unit: string): string {
  return unit === 'm²' || unit === 'm' ? `${qty.toFixed(2)} ${unit}` : `${qty} ${unit}`
}

function finishTitle(f: FinishLine, signage = false): string {
  const remarks = (f.remarks || []).map((r) => r.trim()).filter(Boolean)
  const remarkPart = remarks.length ? ` 备注：${remarks.join('；')}` : ''
  if (f.source === 'process_note' && !(f.area_m2 > 0)) {
    const tag = signage ? SIGNAGE_CATEGORY : '工艺'
    return `${finishName(f)}（${tag}）${remarkPart}`
  }
  return `${finishName(f)}：${qtyText(f.area_m2, f.qty_unit === 'm' ? 'm' : 'm²')}${remarkPart}`
}

function fixtureTitle(f: FixtureLine): string {
  const remarks = (f.remarks || []).map((r) => r.trim()).filter(Boolean)
  const remarkPart = remarks.length ? ` 备注：${remarks.join('；')}` : ''
  const unit = isElectricalFixture(f) ? '个' : '盏'
  return `${fixtureName(f)}：${qtyText(f.count, unit)}${remarkPart}`
}

function openingName(o: OpeningLine): string {
  return drawingName(o.drawing_name, (o.code || '').trim() ? `[${(o.code || '').trim()}]` : '门')
}

function openingRemark(o: OpeningLine): string {
  const extra = o.kind === 'window' ? '窗' : '门'
  const remarks = (o.remarks || []).map((r) => r.trim()).filter(Boolean)
  return [extra, ...remarks].join('；')
}

function openingTitle(o: OpeningLine): string {
  const unit = o.unit || '樘'
  const extra = openingRemark(o)
  return `${openingName(o)}：${qtyText(o.count, unit)}（${extra}）`
}

function equipmentTitle(e: EquipmentLine): string {
  const unit = equipmentUnit(e)
  const cat = equipmentCategory(e)
  const remarks = (e.remarks || []).map((r) => r.trim()).filter(Boolean)
  const remarkPart = remarks.length ? ` 备注：${remarks.join('；')}` : ''
  return `${equipmentName(e)}：${qtyText(e.count, unit)}（${cat}）${remarkPart}`
}

function withSheetTags(
  title: string,
  sheetNos: string[] | undefined,
  onOpenSheets?: (nos: string[]) => void,
) {
  const nos = unionSheetNos(sheetNos)
  if (!nos.length) return title
  return (
    <span>
      {title}{' '}
      {nos.map((n) => (
        <Tag
          key={n}
          color="blue"
          style={{ cursor: onOpenSheets ? 'pointer' : 'default' }}
          onClick={(e) => {
            e.stopPropagation()
            onOpenSheets?.(nos)
          }}
        >
          {n}
        </Tag>
      ))}
    </span>
  )
}

function roomToNode(room: RoomNode, onOpenSheets?: (nos: string[]) => void): DataNode {
  const signage = isSignageRoom(room)
  const finishNodes: DataNode[] = room.finishes
    .map((f, i) => ({
      key: `${room.space_id}-f-${i}`,
      title: withSheetTags(finishTitle(f, signage), f.sheet_nos, onOpenSheets),
      isLeaf: true,
    }))
  const fixtureNodes: DataNode[] = (room.fixtures || []).map((f, i) => ({
    key: `${room.space_id}-fx-${i}`,
    title: withSheetTags(fixtureTitle(f), f.sheet_nos, onOpenSheets),
    isLeaf: true,
  }))
  const openingNodes: DataNode[] = (room.openings || []).map((o, i) => ({
    key: `${room.space_id}-op-${i}`,
    title: withSheetTags(openingTitle(o), o.sheet_nos, onOpenSheets),
    isLeaf: true,
  }))
  const equipmentNodes: DataNode[] = (room.equipment || []).map((e, i) => ({
    key: `${room.space_id}-eq-${i}`,
    title: withSheetTags(equipmentTitle(e), e.sheet_nos, onOpenSheets),
    isLeaf: true,
  }))
  return {
    key: room.space_id,
    title: room.area_m2 > 0 ? `${room.name}：${room.area_m2.toFixed(2)} m²` : room.name,
    children: [
      ...finishNodes,
      ...openingNodes,
      ...fixtureNodes,
      ...equipmentNodes,
      ...room.children.map((child) => roomToNode(child, onOpenSheets)),
    ],
  }
}

function unboundFolder(
  key: string,
  title: string,
  children: DataNode[],
): DataNode | null {
  if (children.length === 0) return null
  return { key, title, children }
}

interface RoomBillProps {
  bill: SpaceBill
  selectedSpaceId?: string | null
  onSelectSpace?: (spaceId: string) => void
  onOpenSheets?: (sheetNos: string[]) => void
}

function isRoomId(bill: SpaceBill, key: string): boolean {
  let found = false
  walkRooms(bill.rooms, (room) => {
    if (room.space_id === key) found = true
  })
  return found
}

export function RoomBillTree({ bill, selectedSpaceId, onSelectSpace, onOpenSheets }: RoomBillProps) {
  const unbound: DataNode[] = [
    unboundFolder(
      'unbound-fixtures',
      '未绑定灯具',
      (bill.unbound_fixtures || []).map((f, i) => ({
        key: `unbound-fx-${i}`,
        title: withSheetTags(fixtureTitle(f), f.sheet_nos, onOpenSheets),
        isLeaf: true,
      })),
    ),
    unboundFolder(
      'unbound-finishes',
      '未绑定墙材',
      (bill.unbound_finishes || []).map((f, i) => ({
        key: `unbound-wall-${i}`,
        title: withSheetTags(finishTitle(f), f.sheet_nos, onOpenSheets),
        isLeaf: true,
      })),
    ),
    unboundFolder(
      'unbound-equipment',
      '未绑定家具/电器',
      (bill.unbound_equipment || []).map((e, i) => ({
        key: `unbound-eq-${i}`,
        title: withSheetTags(equipmentTitle(e), e.sheet_nos, onOpenSheets),
        isLeaf: true,
      })),
    ),
    unboundFolder(
      'unbound-openings',
      '未绑定门窗',
      (bill.unbound_openings || []).map((o, i) => ({
        key: `unbound-op-${i}`,
        title: withSheetTags(openingTitle(o), o.sheet_nos, onOpenSheets),
        isLeaf: true,
      })),
    ),
  ].filter((n): n is DataNode => n != null)

  const lineCount = countRoomBillLines(bill)
  const roomCount = countRoomBillRooms(bill)
  const treeData: DataNode[] = [
    {
      key: 'shop',
      title: `${bill.shop_name}：${bill.shop_area_m2.toFixed(2)} m²`,
      children: [...bill.rooms.map((room) => roomToNode(room, onOpenSheets)), ...unbound],
    },
  ]

  return (
    <>
      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        {roomCount} 个房间
        <Tag style={{ marginLeft: 8 }}>{lineCount} 条</Tag>
      </Text>
      <Tree
        treeData={treeData}
        defaultExpandAll
        showLine
        selectable
        selectedKeys={selectedSpaceId ? [selectedSpaceId] : []}
        onSelect={(keys) => {
          const id = String(keys[0] || '')
          if (id && isRoomId(bill, id)) onSelectSpace?.(id)
        }}
        style={{ fontSize: 14 }}
      />
      <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
        按房间列出该房间的面层、灯具、门窗、家具、电器。与总汇总同一套数据，只是拆法不同。
      </Text>
    </>
  )
}
